/**
 * Live notices: work that arrived while somebody was looking at another screen.
 *
 * A notice is an interruption, so it is kept to the smallest one that still
 * works: what arrived, one line of detail, and a way to go there. It dismisses
 * itself, because the record it names is already in the inbox and the badge -
 * this is the nudge, not the record of it. The timer pauses while the pointer is
 * over the notice or the keyboard is inside it, so a notice cannot vanish in the
 * half-second between reading it and reaching for it.
 */

import { AlertTriangle, Bell, Info, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import type { LiveNotice } from "@/hooks/useRealtime";
import { toTitleCase } from "@/lib/format";

/** Long enough to read two lines and decide; short enough not to be furniture. */
const DISMISS_AFTER_MS = 9_000;

const ICONS = {
  info: Info,
  warning: Bell,
  critical: AlertTriangle,
} as const;

function Toast({ notice, onDismiss }: { notice: LiveNotice; onDismiss: () => void }) {
  const navigate = useNavigate();
  const [held, setHeld] = useState(false);
  // The callback changes identity on every render of the stack; a ref keeps the
  // timer below from being torn down and restarted each time, which would mean
  // it never actually elapses.
  const dismiss = useRef(onDismiss);
  dismiss.current = onDismiss;

  useEffect(() => {
    if (held) return;
    const timer = setTimeout(() => dismiss.current(), DISMISS_AFTER_MS);
    return () => clearTimeout(timer);
  }, [held]);

  const Icon = ICONS[notice.severity];
  const actionable = Boolean(notice.href);

  return (
    <div
      className="toast"
      data-severity={notice.severity}
      role="status"
      onMouseEnter={() => setHeld(true)}
      onMouseLeave={() => setHeld(false)}
      onFocusCapture={() => setHeld(true)}
      onBlurCapture={() => setHeld(false)}
    >
      <Icon size={16} className="mt-0.5 shrink-0 text-muted-foreground" aria-hidden />
      <div className="min-w-0 flex-1">
        <p className="toast-title">{notice.title}</p>
        {notice.body ? <p className="toast-body line-clamp-2">{notice.body}</p> : null}
        {actionable ? (
          <button
            type="button"
            className="mt-1.5 text-xs font-semibold text-brand hover:underline"
            onClick={() => {
              navigate(notice.href!);
              dismiss.current();
            }}
          >
            {toTitleCase("Open")}
          </button>
        ) : null}
      </div>
      <button
        type="button"
        className="shrink-0 rounded p-0.5 text-muted-foreground hover:text-foreground"
        onClick={() => dismiss.current()}
        aria-label="Dismiss"
      >
        <X size={14} aria-hidden />
      </button>
    </div>
  );
}

export function Toasts({
  notices,
  onDismiss,
}: {
  notices: LiveNotice[];
  onDismiss: (id: number) => void;
}) {
  if (notices.length === 0) return null;
  return (
    // Polite rather than assertive: a task arriving is worth announcing at the
    // next pause, never worth interrupting what a screen reader is mid-sentence
    // through - which on a results screen is somebody's measurement.
    <div className="toast-stack" aria-live="polite" aria-relevant="additions">
      {notices.map((notice) => (
        <Toast key={notice.id} notice={notice} onDismiss={() => onDismiss(notice.id)} />
      ))}
    </div>
  );
}
